import React, { useState } from 'react';
import StoreBadge from './StoreBadge';
import styles from './ResultsTable.module.css';

const STORE_ORDER = ['amazon', 'walmart', 'homedepot', 'target', 'ebay', 'bestbuy', 'lowes', 'macys', 'nordstrom', 'costco'];

// Always build a real search URL from product title + brand.
// Agent-generated URLs are mock/fake and must never be used directly.
function getUrl(row) {
  const q = encodeURIComponent(
    [row.brand, row.item].filter(Boolean).join(' ').trim()
  );
  switch (row.store) {
    case 'amazon':    return `https://www.amazon.com/s?k=${q}`;
    case 'walmart':   return `https://www.walmart.com/search?q=${q}`;
    case 'homedepot': return `https://www.homedepot.com/s/${q}`;
    case 'target':    return `https://www.target.com/s?searchTerm=${q}`;
    case 'ebay':      return `https://www.ebay.com/sch/i.html?_nkw=${q}`;
    case 'bestbuy':   return `https://www.bestbuy.com/site/searchpage.jsp?st=${q}`;
    case 'lowes':     return `https://www.lowes.com/search?searchTerm=${q}`;
    case 'macys':     return `https://www.macys.com/shop/featured/${q}`;
    case 'nordstrom': return `https://www.nordstrom.com/sr?origin=keywordsearch&keyword=${q}`;
    case 'costco':    return `https://www.costco.com/CatalogSearch?keyword=${q}`;
    case 'ulta':      return `https://www.ulta.com/search?search=${q}`;
    case 'sephora':   return `https://www.sephora.com/search?keyword=${q}`;
    case 'macys':     return `https://www.macys.com/shop/featured/${q}`;
    default:          return `https://www.google.com/search?q=${q}&tbm=shop`;
  }
}

// ── Normalize title for grouping ──────────────────────────────────
function normalizeTitle(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\b(the|a|an|with|and|for|in|of|oz|ml|lb|pack|set|piece|count|ct|new)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .slice(0, 4)
    .join(' ');
}

// ── Group results by product ──────────────────────────────────────
function groupResults(ranked) {
  const groups = {};
  ranked.forEach(row => {
    const key = normalizeTitle(row.item);
    if (!groups[key]) {
      groups[key] = { key, title: row.item, brand: row.brand, rating: row.rating, stores: {} };
    }
    if (!groups[key].stores[row.store] || row.price < groups[key].stores[row.store].price) {
      groups[key].stores[row.store] = row;
    }
    if (!groups[key].brand && row.brand) groups[key].brand = row.brand;
  });
  return Object.values(groups).sort((a, b) => {
    const aMin = Math.min(...Object.values(a.stores).map(s => s.price));
    const bMin = Math.min(...Object.values(b.stores).map(s => s.price));
    return aMin - bMin;
  });
}

// ── Single product card ───────────────────────────────────────────
function ProductCard({ group, activeStoreIds, storeMap }) {
  const prices  = Object.values(group.stores).map(s => s.price);
  const minP    = Math.min(...prices);
  const maxP    = Math.max(...prices);
  const spread  = prices.length > 1 ? +(maxP - minP).toFixed(2) : 0;
  const storeCount = Object.keys(group.stores).length;

  // Sort stores: carried first (by price asc), then not carried
  const orderedStores = [
    ...activeStoreIds.filter(id => group.stores[id]).sort((a, b) => group.stores[a].price - group.stores[b].price),
    ...activeStoreIds.filter(id => !group.stores[id]),
  ];

  return (
    <div className={styles.card}>
      {/* Card header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardLeft}>
          <div className={styles.productTitle}>{group.title}</div>
          <div className={styles.productMeta}>
            {group.brand && <span className={styles.brandTag}>{group.brand}</span>}
            {group.rating && <span className={styles.ratingTag}>★ {group.rating}</span>}
            <span className={styles.storeCountTag}>{storeCount} store{storeCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
        {spread > 0 && (
          <div className={styles.spreadBadge}>
            ⚡ Save ${spread.toFixed(2)}
          </div>
        )}
      </div>

      {/* Store rows */}
      <div className={styles.storeRows}>
        {orderedStores.map(id => {
          const row    = group.stores[id];
          const store  = storeMap[id];
          const isBest = row && row.price === minP;

          if (!store) return null;

          return (
            <div key={id} className={`${styles.storeRow} ${isBest ? styles.bestStoreRow : ''} ${!row ? styles.naRow : ''}`}>
              {/* Store badge */}
              <div className={styles.storeCell}>
                <StoreBadge store={store} />
              </div>

              {/* Price or N/A */}
              {row ? (
                <>
                  <div className={styles.priceCell}>
                    <span className={isBest ? styles.priceBest : styles.priceMid}>
                      ${row.price.toFixed(2)}
                    </span>
                    {row.original_price && row.original_price > row.price && (
                      <span className={styles.wasPrice}>${row.original_price.toFixed(2)}</span>
                    )}
                  </div>

                  {/* Badges */}
                  <div className={styles.badgeCell}>
                    {isBest && <span className={styles.badge} style={{ background: '#dcfce7', color: '#16a34a' }}>Best price</span>}
                    {row.shipping_free && <span className={styles.badge} style={{ background: '#eff6ff', color: '#2563eb' }}>Free ship</span>}
                    {row.is_prime && <span className={styles.badge} style={{ background: '#fef3c7', color: '#92400e' }}>Prime</span>}
                    {row.is_two_day && <span className={styles.badge} style={{ background: '#eff6ff', color: '#1d4ed8' }}>2-Day</span>}
                    {row.is_drive_up && <span className={styles.badge} style={{ background: '#f0fdf4', color: '#16a34a' }}>Drive Up</span>}
                    {row.has_circle_deal && <span className={styles.badge} style={{ background: '#fef2f2', color: '#b91c1c' }}>⭕ Circle</span>}
                    {row.bulk_price > 0 && <span className={styles.badge} style={{ background: '#fff7ed', color: '#c2410c' }}>Bulk deal</span>}
                    {row.discount_pct > 0 && <span className={styles.badge} style={{ background: '#f0fdf4', color: '#15803d' }}>-{row.discount_pct}%</span>}
                  </div>

                  {/* Buy button */}
                  <div className={styles.buyCell}>
                    <a
                      href={getUrl(row)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={isBest ? styles.btnBest : styles.btnGhost}
                    >
                      {isBest ? 'Buy now ↗' : 'Buy ↗'}
                    </a>
                  </div>
                </>
              ) : (
                <div className={styles.naCell} colSpan={3}>
                  <span className={styles.naText}>Not available</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div style={{ flex: 1 }}>
          <div className={styles.sk} style={{ width: '60%', height: 16, marginBottom: 8 }} />
          <div className={styles.sk} style={{ width: '30%', height: 12 }} />
        </div>
      </div>
      <div className={styles.storeRows}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className={styles.storeRow}>
            <div className={styles.sk} style={{ width: 80, height: 24, borderRadius: 12 }} />
            <div className={styles.sk} style={{ width: 60, height: 16, marginLeft: 16 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ranked list row ───────────────────────────────────────────────
function RankedRow({ row, isBest, isWorst, maxPrice, storeMap }) {
  const saving = maxPrice - row.price;
  const url    = getUrl(row);
  return (
    <tr className={`${styles.row} ${isBest ? styles.bestRow : ''}`}>
      <td>
        <div className={styles.rankCell}>
          <div className={`${styles.rankBadge} ${
            row.rank === 1 ? styles.rank1 :
            row.rank === 2 ? styles.rank2 :
            row.rank === 3 ? styles.rank3 : styles.rankN}`}>
            {row.rank === 1 ? '🥇' : row.rank}
          </div>
          <span className={styles.rating}>★ {row.rating}</span>
        </div>
      </td>
      <td><div className={styles.itemName}>{row.item}</div></td>
      <td><span className={styles.brand}>{row.brand}</span></td>
      <td><StoreBadge store={storeMap[row.store]} /></td>
      <td className={styles.right}>
        <span className={isBest ? styles.priceBest : isWorst ? styles.priceWorst : styles.priceMid}>
          ${row.price.toFixed(2)}
        </span>
        {isBest  && <div className={`${styles.ptag} ${styles.ptagBest}`}>Best price</div>}
        {isWorst && <div className={`${styles.ptag} ${styles.ptagHigh}`}>Highest</div>}
        {!isBest && !isWorst && saving > 5 &&
          <div className={`${styles.ptag} ${styles.ptagSave}`}>Save ${saving.toFixed(2)}</div>}
      </td>
      <td className={styles.buyCell}>
        <a href={url} target="_blank" rel="noopener noreferrer"
          className={isBest ? styles.btnBest : styles.btnGhost}>
          {isBest ? 'Buy now ↗' : 'Buy ↗'}
        </a>
      </td>
    </tr>
  );
}

// ── Main export ───────────────────────────────────────────────────
export default function ResultsTable({ ranked, storeMap, loading, activeCount }) {
  const [viewMode, setViewMode] = useState('grouped');

  const activeStoreIds = STORE_ORDER.filter(id => storeMap[id]);
  const groups   = groupResults(ranked);
  const minPrice = ranked.length ? Math.min(...ranked.map(r => r.price)) : 0;
  const maxPrice = ranked.length ? Math.max(...ranked.map(r => r.price)) : 0;

  return (
    <div className={styles.wrap}>

      {/* Top bar */}
      <div className={styles.topBar}>
        <span className={styles.title}>
          {ranked.length > 0
            ? viewMode === 'grouped'
              ? `${groups.length} products · ${ranked.length} listings`
              : `${ranked.length} listings`
            : 'Results'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={styles.meta}>
            {ranked.length > 0 ? `${activeCount} stores` : loading ? 'scanning…' : ''}
          </span>
          {ranked.length > 0 && (
            <div className={styles.viewToggle}>
              <button className={`${styles.toggleBtn} ${viewMode === 'grouped' ? styles.toggleActive : ''}`}
                onClick={() => setViewMode('grouped')}>
                Apple-to-Apple
              </button>
              <button className={`${styles.toggleBtn} ${viewMode === 'ranked' ? styles.toggleActive : ''}`}
                onClick={() => setViewMode('ranked')}>
                Ranked List
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Grouped card view */}
      {viewMode === 'grouped' && (
        <div className={styles.cardList}>
          {loading && groups.length === 0 &&
            [1,2,3].map(i => <SkeletonCard key={i} />)}
          {groups.map(group => (
            <ProductCard
              key={group.key}
              group={group}
              activeStoreIds={activeStoreIds}
              storeMap={storeMap}
            />
          ))}
        </div>
      )}

      {/* Ranked table view */}
      {viewMode === 'ranked' && (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Rank</th><th>Item</th><th>Brand</th>
                <th>Marketplace</th><th className={styles.right}>Price</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && ranked.length === 0 &&
                Array.from({length:6}).map((_,i) => (
                  <tr key={i} className={styles.skRow}>
                    {[60,200,100,80,70,60].map((w,j) => (
                      <td key={j}><div className={styles.sk} style={{width:w}} /></td>
                    ))}
                  </tr>
                ))}
              {ranked.map((row, i) => (
                <RankedRow key={`${row.store}-${i}`} row={row}
                  isBest={row.price === minPrice} isWorst={row.price === maxPrice}
                  maxPrice={maxPrice} storeMap={storeMap} />
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
